import {ThemePalette} from '@angular/material/core';
import {
  DocPackage,
  DocPackageVersion,
  ElementsGptIndexMeta,
  Implementation,
  ImportedDocPackage,
  Refmodel,
  SalesforceConnection,
  BaseDialogData,
  MetadataUserList,
  UserRefModelInfo,
  User,
  UserTeam
} from '@q9elements/ui-kit/common';
import {Observable} from 'rxjs';

export enum ORG_STATE {
  SUCCESS = 'success',
  ERROR = 'error'
}

export enum ORG_TYPE {
  PRODUCTION = 'production',
  SANDBOX = 'sandbox'
}

export enum ORG_ROLE {
  MANAGE = 'manage',
  EDIT = 'edit',
  VIEW = 'view',
  NO_ACCESS = 'no access'
}

export enum PACKAGES_SUB_TUBS {
  IMPORTED = 'SF_CONNECTIONS.PACKAGES_TAB_IMPORTED',
  DOC_PACKAGE = 'SF_CONNECTIONS.DOC_PACKAGE_TAB'
}

export enum IMPORT_DATA_TYPE_ENTERPRISE {
  STAKEHOLDERS = 'stakeholders',
  OPTIMIZATION_ASSESSMENT = 'optimization_assessment',
  TAGS = 'tags',
  URLS = 'urls'
}

export enum SYNC_STATE {
  UP_TO_DATE = 'Up to date',
  OUT_OF_DATE = 'Out of date'
}

export interface Q9maRefmodel extends Refmodel {
  syncStarted: boolean;
  linkInApp: string;
  sfConnection?: SalesforceConnection;
  syncProgress: {
    progress: string;
    currentStep: {
      displayName: string;
      name: string;
    };
  } | null;
  orgStatus: {
    state: ORG_STATE.SUCCESS | ORG_STATE.ERROR;
    message: string;
  } | null;
  indexMeta?: ElementsGptIndexMeta;
  orgProcessing?: boolean;
  deletionScheduledAt?: string;
  orgCreditScore: number;
  isSchedulingEnabled?: boolean;
  manualSyncOnlyEnabled?: boolean;
  userList?: MetadataUserList[];
  additionalEmail?: string;
  applyFirstTimeDefaultRoot?: boolean;
  firstTimeNavigationNode?: string;
}

export interface Q9maImplementation extends Implementation {
  daysLeft?: number;
  docPackage?: (DocPackage & {docPackageVersions: DocPackageVersion[]}) | null;
  importedPackages?: ImportedDocPackage[];
  reactivatingInProgress?: boolean;
}

export interface ImplementationsPayload {
  implementationsList: Q9maImplementation[];
  list: Q9maRefmodel[];
  total: number;
  totalWithNonAccessible: number;
}

export interface ConnectionQueryParamsInterface {
  client_id: string | undefined;
  prompt?: 'login' | 'consent' | 'select_account';
  redirect_uri: string;
  response_type: 'code';
  code_challenge?: string;
  code_challenge_method?: 'S256';
  immediate?: boolean;
  state: {
    orgId: string;
    type?: 'consulting' | 'external';
    teamId?: string | undefined;
    teamIndex?: string | number | undefined;
    connectOrg?: boolean;
    implementationId?: string;
    isSandbox: boolean;
    reconnectOrg?: boolean;
    pkceNonce?: string;
    startSync?: boolean;
    refModelId?: string;
    manualSyncOnlyEnabled?: boolean;
    isNewImplementation?: boolean;
  };
}

export enum SYNC_PROGRESS_STEP {
  FINISH = 'finish_sync',
  SYNC_FINISHED = 'sync_finished'
}

export interface CreateDocPackageVersionDialogPayload {
  implementation: Q9maImplementation;
  lastPublishedVersion: DocPackage['lastPublishedVersion'];
}

export interface CreateDocPackageVersionPayload {
  version: number[];
  implementationId: string;
  activeState: boolean;
}

export interface CreateNewPackagePayload {
  name: string;
  description?: string;
  implementation: string;
}

export interface EditImplementationPayload {
  name: string;
  description?: string;
}

export interface EditSfOrganizationPayload extends EditImplementationPayload {}

export type RefModelTogglesType =
  | 'gdprEnabled'
  | 'dataPopulationV3Enabled'
  | 'manualSyncOnlyEnabled';

export interface StartSyncPayload {
  refModelId: string;
  teamId?: string;
  teamIndex?: string;
  refModelType?: string;
}

export interface RefreshSfTokenResponse {
  tokenUpdated?: boolean;
  orgId?: string;
  hour?: number;
  isReconnect?: boolean;
  reconnectTo?: string;
  newOrgId?: string;
  alreadyConnected?: boolean;
  username?: string;
  isSuccess?: boolean;
  orgReconnected?: boolean;
}

export interface GeneralOrgInfoResponse {
  result: {
    username: string;
    orgId: string;
    orgType: ORG_TYPE;
    instance_url: string;
    alreadyConnected: boolean;
    connectedUsername: string | null;
    connectedSpaceName: string | null;
  };
  isSuccess: boolean;
}

export interface ConnectOrgResponse {
  isSuccess: boolean;
  refModelId: string;
}

export interface ParsedSfQueryParams {
  teamId: number;
  implementationId: string;
  isSandbox: boolean;
  code: string;
  reconnectOrg: boolean;
  teamIndex: number;
  type: string;
}

export interface DocPackageImportSource {
  enterprise?: string;
  isPublic?: boolean;
  createdAt?: string;
  version?: string;
  _id?: string;
  name?: string;
}

export interface ImportMetaData {
  uploadUrl: string | ((...args: unknown[]) => string);
  actionType: string;
}

export interface GenerateCsvTemplatePayload {
  importType: string;
  implementationId: string;
  scope: string[];
  allNodes: boolean;
}

export interface SfCallbackParams {
  code: string;
  teamIndex: string;
  teamId: string;
  connectOrg: string;
  implementationId: string;
  isSandbox: string;
  orgId: string;
  type: string;
  reconnectOrg: string;
  oldOrgId?: string;
  state: string;
  startSync?: string;
  refModelId?: string;
  manualSyncOnlyEnabled?: string;
  isNewImplementation?: string;
}

export interface SfConnectionInfoDialogData extends BaseDialogData {
  alreadyConnected: boolean;
  isReconnect: boolean;
  orgId: string;
  username: string;
  uiTime: string;
  orgDomain?: string;
  spaceId?: string;
  isSandbox?: boolean;
  manualSyncOnlyEnabled: boolean;
}

export interface SfConnectionErrorDialogData extends BaseDialogData {
  orgId: string;
  username: string;
  connectedUsername: string | null;
  connectedSpaceName: string | null;
  orgDomain?: string;
  isSandbox?: boolean;
  error: {
    errorData: {
      error: string;
      error_description?: string;
      status?: string;
      message?: string;
    };
    errorSource: string;
  };
}

export interface SetSchedulingDialogData extends BaseDialogData {
  sfConnection: Q9maRefmodel['sfConnection'];
  action: string;
  onSubmit: (value: string | Date) => Observable<number>;
}

export interface SelectMetadataScopeDialogData extends BaseDialogData {
  sfOrg: Q9maRefmodel;
}

export interface EditImplementationDialogData extends BaseDialogData {
  implementation: Q9maImplementation;
}

export interface DisconnectOrganizationDialogData extends BaseDialogData {
  teamId: string;
  orgId: string;
}

export interface CreatePackageVersionDialogData extends EditImplementationDialogData {
  lastPublishedVersion: DocPackage['lastPublishedVersion'];
}

export interface AddAdditionalEmailDialogData extends SelectMetadataScopeDialogData {
  clearEmail: boolean;
}

export type OrgGeneralInfoPayload = ParsedSfQueryParams &
  GeneralOrgInfoResponse['result'] & {
    uiTime: string;
    isSuccess: boolean;
    refModelId?: string;
  };

export interface SfUser extends User, UserRefModelInfo {
  canCopy: boolean | number;
  canGrantAccess: boolean | number;
  canChangeFingerprint: boolean | number;
  teamPermission: UserTeam;
}

export interface SfUsersResponse {
  refModelOwner: SfUser;
  users: SfUser[];
  count: {
    view: number;
    edit: number;
    manage: number;
    spaceView: number;
    author: number;
  };
}

export interface SelectMetadataPersonalScopeDialogData {
  color: ThemePalette;
  sfOrg: Q9maRefmodel;
  user: User;
}

export interface SfDetailsItem {
  title: string;
  icon: string;
  value: {
    toString(): string;
  };
}

export interface SyncStateDisplay {
  key: string;
  bgColor: string;
  fontColor: string;
}

export type SyncStateDisplayMap = Record<'UP_TO_DATE' | 'OUT_OF_DATE', SyncStateDisplay>;

export interface ComplexityLevel extends SyncStateDisplay {
  threshold: number;
}

export type ComplexityLevelMap = Record<'STANDARD' | 'MODERATE' | 'COMPLEX', ComplexityLevel>;

export const ALREADY_CONNECTED_ERR = 'already_connected';

export interface SuspendedState {
  isReactivateVisible: boolean;
  isReactivateDisabled: boolean;
  isDeleteVisible: boolean;
  isDeleteDisabled: boolean;
}
